import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { useAutoRefetch } from '@/lib/useAutoRefetch';
import { fmtDate } from '@/lib/format';
import { Briefcase, AlertTriangle, Clock, ShieldAlert, PauseCircle, Hourglass, Wallet } from 'lucide-react';

const SECTION_META = {
  actions_due_week:    { label: 'Actions due this week', icon: Clock,       tone: 'info',    hint: 'Deliver by end of week to stay on-time.' },
  actions_overdue:     { label: 'Overdue actions',       icon: AlertTriangle, tone: 'danger', hint: 'Past due date. Log an update or reset the date.' },
  actions_blocked:     { label: 'Blocked',               icon: ShieldAlert, tone: 'danger',  hint: 'Blocker recorded — escalate if unresolved.' },
  actions_waiting_client: { label: 'Waiting for client', icon: PauseCircle, tone: 'warning', hint: 'Client-controlled — visibility only, no KRA impact.' },
  actions_stale:       { label: 'No update in >7 days',  icon: Hourglass, tone: 'warning', hint: 'Log any progress note to reset the stale timer.' },
  approvals_overdue:   { label: 'Approvals overdue',     icon: AlertTriangle, tone: 'danger', hint: 'Client is past expected reply date.' },
  approvals_awaiting:  { label: 'Approvals awaiting',    icon: Clock,       tone: 'info',    hint: 'Client-controlled reply pending.' },
  collections_due_today: { label: 'Collections due today', icon: Wallet, tone: 'danger',    hint: 'PI due date is today.' },
  collections_overdue: { label: 'Collections overdue',   icon: AlertTriangle, tone: 'danger', hint: 'Follow up urgently.' },
  collections_missing_followup: { label: 'PIs missing next follow-up', icon: Hourglass, tone: 'warning', hint: 'Schedule a next follow-up date.' },
};

const SECTION_ORDER = [
  'actions_due_week', 'actions_overdue', 'actions_blocked',
  'actions_waiting_client', 'actions_stale',
  'approvals_overdue', 'approvals_awaiting',
  'collections_due_today', 'collections_overdue', 'collections_missing_followup',
];

export default function MyWorkPage() {
  const { user } = useAuth();
  const [payload, setPayload] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get('/reports/kra/my-work');
      setPayload(r.data);
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useAutoRefetch(load, {
    collections: ['actions', 'client_approvals', 'collections_ledger'],
  });

  const sections = payload?.sections || {};
  const totalOpen = SECTION_ORDER.reduce((s, k) => s + (sections[k]?.length || 0), 0);

  return (
    <AppShell>
      <div className="space-y-4" data-testid="my-work-page">
        <div className="flex items-center gap-2">
          <Briefcase size={20} className="text-[hsl(var(--primary))]" />
          <div>
            <h1 className="text-[22px] font-semibold" data-testid="my-work-title">My Work</h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))]">
              Everything the studio has committed to <strong>you</strong> — grouped by urgency. Client-controlled waits are visible but never scored against you.
            </p>
          </div>
        </div>

        {busy && !payload && (
          <div className="text-center py-16 text-[hsl(var(--foreground-muted))]">Loading…</div>
        )}

        {payload && totalOpen === 0 && (
          <div className="border rounded-xl p-8 text-center text-[hsl(var(--foreground-muted))]"
               style={{ borderColor: 'hsl(var(--border))' }}
               data-testid="my-work-empty">
            🌤 All caught up. No open actions, approvals, or collections assigned to you.
          </div>
        )}

        <div className="space-y-4" data-testid="my-work-sections">
          {SECTION_ORDER.map(key => {
            const rows = sections[key] || [];
            if (rows.length === 0) return null;
            return <MyWorkSection key={key} sectionKey={key} rows={rows} />;
          })}
        </div>
      </div>
    </AppShell>
  );
}

function MyWorkSection({ sectionKey, rows }) {
  const meta = SECTION_META[sectionKey] || { label: sectionKey, icon: Clock, tone: 'default' };
  const Icon = meta.icon;
  const border = {
    danger: 'border-[hsl(0_60%_78%)]',
    warning: 'border-[hsl(38_80%_75%)]',
    info: 'border-[hsl(210_70%_82%)]',
    default: 'border-[hsl(var(--border))]',
  }[meta.tone];
  return (
    <div className={`border rounded-xl bg-[hsl(var(--card))] ${border}`}
         data-testid={`my-work-section-${sectionKey}`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
        <Icon size={15} />
        <h2 className="text-[14px] font-semibold">{meta.label}</h2>
        <span className="chip text-[10.5px]">{rows.length}</span>
        {meta.hint && (
          <span className="text-[11.5px] text-[hsl(var(--foreground-muted))] ml-auto italic">{meta.hint}</span>
        )}
      </div>
      <table className="tbl" data-testid={`my-work-table-${sectionKey}`}>
        <thead><tr>
          <th>Item</th><th>Project</th><th>Due</th><th>Status</th><th>Note</th><th></th>
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} data-testid={`my-work-row-${sectionKey}-${r.id}`}>
              <td className="font-medium">{r.title || <em className="text-[hsl(var(--foreground-subtle))]">—</em>}</td>
              <td>
                {r.project_id ? (
                  <Link to={`/projects/${r.project_id}`}
                        className="text-[hsl(var(--primary))] hover:underline text-[12px]">
                    {r.project_name || r.project_id.slice(0, 8)}
                  </Link>
                ) : '—'}
              </td>
              <td className="text-[12px]">{r.due_date ? fmtDate(r.due_date) : '—'}</td>
              <td>{r.status ? <span className="chip text-[10.5px]">{r.status.replace(/_/g, ' ')}</span> : '—'}</td>
              <td className="text-[11.5px] text-[hsl(var(--foreground-muted))] max-w-[180px] truncate">
                {r.blocker || (r.next_followup_date ? `Next follow-up ${fmtDate(r.next_followup_date)}` : '—')}
              </td>
              <td className="text-right">
                {r.project_id && (
                  <Link to={`/projects/${r.project_id}?tab=followups`}
                        className="text-[11px] text-[hsl(var(--primary))] hover:underline"
                        data-testid={`my-work-open-${r.id}`}>
                    Open →
                  </Link>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
